import pluralize from "pluralize";
import { getAmountCodes } from "../constants/storageAmounts";
import { UnitType } from "encamp-prisma/generated/client";
import {
  AK_MIN_AMOUNT_DECIMAL_PLACES,
  DEFAULT_MIN_AMOUNT_DECIMAL_PLACES,
} from "../units/minAmounts";
import { prettyPrintEnumValue } from "./prettyPrintEnumValue";

pluralize.addSingularRule(/feet$/i, "foot");
pluralize.addPluralRule(/feet$/i, "feet");

/**
 * Formats a quantity with its unit for display.
 * @param amount - The numeric quantity
 * @param unit - The unit type
 * @param abbreviateUnit - Whether to use abbreviated unit labels
 * @param includeLabel - Whether to include the unit label in the output
 * @param state - The state of the quantity, for state specific decimal formatting (AK requires more precision than other states)
 * @returns Formatted string representation of the quantity
 */
export function prettyPrintQuantity({
  amount,
  unit,
  includeUnit = true,
  abbreviateUnit = true,
  includeLabel = true,
  state = null,
}: {
  amount?: number | null;
  unit?: UnitType | null;
  includeUnit?: boolean;
  abbreviateUnit?: boolean;
  includeLabel?: boolean;
  state?: string | null;
}): string {
  if (!amount && amount !== 0) return "no data";

  let decimalPlaces = DEFAULT_MIN_AMOUNT_DECIMAL_PLACES;
  if (state && state === "AK") {
    decimalPlaces = AK_MIN_AMOUNT_DECIMAL_PLACES;
  }

  // For very small numbers (< 0.001), toLocaleString can use scientific notation
  // Use toFixed with high precision to show actual value precision and avoid scientific notation
  // For normal numbers, use toLocaleString with state-specific decimal places
  const formattedAmount =
    Math.abs(amount) < 0.001
      ? amount.toFixed(15).replace(/\.?0+$/, "") // Use 15 decimals to capture precision, remove trailing zeros
      : amount.toLocaleString(undefined, {
          maximumFractionDigits: decimalPlaces,
        });

  if (!includeLabel) {
    return formattedAmount;
  }

  const unitLabel = includeUnit
    ? getUnitLabel({ unit, amount, abbreviateUnit })
    : "";

  return [formattedAmount, unitLabel].join(" ");
}

/**
 * Formats an amount with its unit for display.
 *
 * Use this when you want to round down to the nearest integer.
 * This is important for displaying max and average amounts
 * e.g. 49,999.9999 -> 49,999 lbs
 *
 * If you want to display 50,000 in that case, use `prettyPrintQuantity`
 *
 * @param amount - The numeric quantity
 * @param unit - The unit type
 * @param abbreviateUnit - Whether to use abbreviated unit labels
 * @returns Formatted string representation of the amount
 */
export function prettyPrintAmount(
  amount?: number | null,
  unit?: UnitType | null,
  abbreviateUnit = false
): string {
  return prettyPrintQuantity({
    amount: amount ? Math.floor(amount) : null,
    unit,
    abbreviateUnit,
    includeLabel: true,
  });
}

/**
 * Formats a large quantity with its unit for display.
 * @param amount - The numeric quantity
 * @param unit - The unit type
 * @param abbreviateUnit - Whether to use abbreviated unit labels
 * @param includeLabel - Whether to include the unit label in the output
 * @returns Formatted string representation of the quantity
 */
export function prettyPrintLargeQuantity(
  amount?: number | null,
  unit?: UnitType | null,
  abbreviateUnit = false,
  includeLabel = true
): string {
  if (!amount && amount !== 0) return "no data";

  const abbreviations = [
    { value: 1e12, symbol: "T" },
    { value: 1e9, symbol: "B" },
    { value: 1e6, symbol: "M" },
    { value: 1e3, symbol: "K" },
    { value: 1, symbol: "" },
  ];

  let formattedAmount = "";
  for (const { value, symbol } of abbreviations) {
    if (Math.abs(amount) >= value) {
      formattedAmount = (amount / value).toFixed(2);

      // Ensure we have no more than 3 numeric characters
      if (Math.abs(parseFloat(formattedAmount)) >= 100) {
        formattedAmount = Math.round(parseFloat(formattedAmount)).toString();
      } else if (Math.abs(parseFloat(formattedAmount)) >= 10) {
        formattedAmount = parseFloat(formattedAmount).toFixed(1);
      }

      // Remove trailing zeros and decimal point if necessary
      formattedAmount = formattedAmount.replace(/\.0+$/, "");
      if (formattedAmount.includes(".")) {
        formattedAmount = formattedAmount.replace(/0+$/, "");
      }

      // Add a space before the symbol if it's not empty
      formattedAmount += symbol ? ` ${symbol}` : "";
      break;
    }
  }

  if (!formattedAmount) {
    formattedAmount = amount.toString();
  }

  if (!includeLabel) {
    return formattedAmount;
  }

  const unitLabel = getUnitLabel({ unit, amount, abbreviateUnit });

  return `${formattedAmount} ${unitLabel}`;
}

/**
 * Formats an amount code with its unit for display.
 * @param state - The state of the amount code
 * @param rawAmountCode - The raw amount code
 * @param unit - The unit type
 * @param abbreviateUnit - Whether to use abbreviated unit labels
 * @param includeLabel - Whether to include the unit label in the output
 * @returns Formatted string representation of the amount code
 */
export function prettyPrintAmountCode(
  state?: string | null,
  rawAmountCode?: number | null,
  unit?: UnitType | null,
  abbreviateUnit = false,
  includeLabel = true
) {
  if (!rawAmountCode) return "no data";

  const codes = getAmountCodes(state ?? "");
  const amountCode = codes[rawAmountCode.toString()] ?? "";

  if (!includeLabel) {
    return `${amountCode}`;
  }

  const unitLabel = getUnitLabel({ unit, abbreviateUnit });

  return `${amountCode} ${unitLabel}`;
}

/**
 * Gets the unit label based on the unit type and amount.
 * @param unit - The unit type
 * @param amount - The numeric quantity
 * @param abbreviateUnit - Whether to use abbreviated unit labels
 * @returns The unit label
 */
function getUnitLabel({
  unit,
  amount,
  abbreviateUnit = false,
}: {
  unit?: UnitType | null;
  amount?: number | null;
  abbreviateUnit?: boolean;
}) {
  if (abbreviateUnit) {
    return unit ? translateUnitToLabel(unit) : "(No unit)";
  }

  return unit
    ? pluralize(prettyPrintEnumValue(unit), amount ?? undefined)
    : "(No unit)";
}

/**
 * Translates a UnitType to its abbreviated label.
 * @param unit - The unit type to translate
 * @returns The abbreviated label for the given unit
 * @throws Error if an unknown unit type is provided
 */
function translateUnitToLabel(unit: UnitType): string {
  switch (unit) {
    case UnitType.BARRELS:
      return "bbls";
    case UnitType.CUBIC_FEET:
      return "cu. ft";
    case UnitType.GALLONS:
      return "gals";
    case UnitType.KILOGRAMS:
      return "kg";
    case UnitType.LITERS:
      return "L";
    case UnitType.METRIC_TONS:
      return "MT";
    case UnitType.POUNDS:
      return "lbs";
    case UnitType.TONS:
      return "T";
    default:
      throw new Error(`Unknown unit type: ${unit}`);
  }
}
